import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { MarketingTriggerSummary } from "@shared-core/data/marketing-types";
import { formatCount, formatDateOrDash } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { MarketingNav } from "../app/components/domain/marketing/MarketingNav";
import { Badge } from "../app/components/primitives/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";

/**
 * Marketing › Triggers: every triggered email, whether it's live, and how
 * many people are waiting on today's list. See
 * docs/features/triggered-emails/01_product_brief.md.
 */
export function MarketingTriggersPage() {
  const dataAdapter = useDataAdapter();
  const [triggers, setTriggers] = useState<MarketingTriggerSummary[] | null>(
    null,
  );
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    dataAdapter
      .listMarketingTriggers()
      .then((rows) => !cancelled && setTriggers(rows))
      .catch((e) => {
        console.error("marketing.triggers.load.failed", e);
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  return (
    <div className="space-y-6">
      <MarketingNav active="triggers" />
      <div>
        <h1 className="text-2xl mb-1">Triggers</h1>
        <p className="text-sm text-muted-foreground">
          Emails that go out by themselves when something happens to a customer.
          Nobody gets the same trigger email twice, even if they keep matching
          it. To follow someone up later, add a separate trigger with its own
          email.
        </p>
      </div>

      {error && (
        <div className="text-sm p-8 text-center border border-border rounded-lg text-red-600 dark:text-red-400">
          Couldn't load triggers. Please try again.
        </div>
      )}
      {!error && triggers === null && (
        <div className="text-sm text-muted-foreground">Loading triggers…</div>
      )}
      {!error && triggers?.length === 0 && (
        <div className="text-sm p-8 text-center border border-border rounded-lg text-muted-foreground">
          No triggers set up yet.
        </div>
      )}

      {triggers && triggers.length > 0 && (
        <div className="rounded-lg border border-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Trigger</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Check before sending</TableHead>
                <TableHead>Today</TableHead>
                <TableHead>Last sent</TableHead>
                <TableHead className="text-right">Sent, last 30 days</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {triggers.map((t) => (
                <TableRow key={t.trigger_id}>
                  <TableCell className="max-w-md">
                    <Link
                      to={paths.marketingTrigger({ triggerId: t.trigger_id })}
                      className="font-medium text-foreground hover:text-primary hover:underline"
                    >
                      {t.name}
                    </Link>
                    <div className="text-xs text-muted-foreground mt-0.5 whitespace-normal">
                      {t.description}
                    </div>
                  </TableCell>
                  <TableCell>
                    <TriggerStatusBadge status={t.status} />
                  </TableCell>
                  <TableCell>{t.check_before_sending ? "Yes" : "No"}</TableCell>
                  <TableCell>
                    {t.status === "paused" ? (
                      <span className="text-muted-foreground">—</span>
                    ) : t.today_to_check > 0 ? (
                      <Link
                        to={paths.marketingTrigger({ triggerId: t.trigger_id })}
                        className="font-medium text-primary hover:underline"
                      >
                        {t.today_to_check} to check
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">
                        Nothing to check
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{formatDateOrDash(t.last_sent_at)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCount(t.sent_last_30_days)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export function TriggerStatusBadge({
  status,
}: {
  status: MarketingTriggerSummary["status"];
}) {
  return status === "live" ? (
    <Badge variant="outline" className="gap-1.5">
      <span className="size-2 rounded-full bg-emerald-500" aria-hidden />
      Live
    </Badge>
  ) : (
    <Badge variant="outline" className="gap-1.5 text-muted-foreground">
      <span
        className="size-2 rounded-full bg-muted-foreground/50"
        aria-hidden
      />
      Paused
    </Badge>
  );
}
